<template>
  <div id="due-diligence-approval-queue">
    <v-row>
      <v-col cols="12">
        <material-card
        :elevation="0"
        full-width>
          <template #header>
            <v-row no-gutters align="center">
              <v-col>
                <p class="headline font-weight-regular mb-2">
                  {{ dueDiligenceApprovalEnabled() ? $t('dueDiligenceApproval') : $t('decidedDueDiligence') }}
                </p>
              </v-col>
              <v-col cols="auto">
                <v-progress-circular v-if="loading" indeterminate size="22" width="2" color="white" class="mx-1"/>
                <Icon
                v-else
                icon="mdi-refresh"
                :tooltipText="$t('refresh')"
                :small="false"
                iconColor="white"
                @icon-clicked="refresh"
                elementId="due-diligence-refresh"/>
              </v-col>
            </v-row>
          </template>

          <v-tabs v-if="dueDiligenceApprovalEnabled()" v-model="tab" color="primary" class="mb-3">
            <v-tab>{{ $t('pendingWithCount', { n: queue.length }) }}</v-tab>
            <v-tab>{{ $t('decided') }}</v-tab>
          </v-tabs>

          <v-row dense class="mb-2" align="center">
            <v-col cols="12" :md="tab === 0 ? 8 : true">
              <v-text-field
              v-model="search"
              :placeholder="$t('searchTractsCountyOrForester')"
              prepend-inner-icon="mdi-magnify"
              color="primary"
              dense
              outlined
              hide-details
              clearable/>
            </v-col>
            <v-col v-if="tab === 0" cols="12" md="2">
              <v-select
              v-model="sortBy"
              :items="sortOptions"
              :label="$t('sortBy')"
              :menu-props="{ offsetY: true }"
              dense
              outlined
              hide-details/>
            </v-col>
            <v-col v-if="tab === 0" cols="12" md="2">
              <v-select
              v-model="teamFilter"
              :items="teamFilterOptions"
              :label="$t('team')"
              :menu-props="{ offsetY: true }"
              dense
              outlined
              hide-details/>
            </v-col>
            <v-col v-else cols="12" md="auto" class="d-flex align-center">
              <DecidedApprovalsFilter :filter="decidedFilter" @filter-applied="applyDecidedFilter"/>
              <!-- The export pages through every matching server page, so it can run long -->
              <v-progress-circular v-if="exporting" indeterminate size="22" width="2" color="primary" class="mx-1"/>
              <Icon
              v-else
              icon="mdi-download"
              @icon-clicked="exportDecided"
              :disabled="!decidedLoaded"
              :small="false"
              iconColor="primary"
              :tooltipText="$t('downloadCsv')"
              margin="mr-1"
              elementId="decided-export-csv"/>
            </v-col>
          </v-row>

          <v-chip-group v-if="tab === 1 && decidedFilterChips.length" column class="mt-n1 mb-1" id="decided-filter-chips">
            <v-chip
            v-for="chip in decidedFilterChips"
            :key="chip.key"
            color="secondary"
            text-color="white"
            small
            close
            @click:close="removeDecidedFilter(chip)">
              <v-icon small left :color="chip.iconColor || 'white'">{{ chip.icon }}</v-icon>
              {{ chip.label }}
            </v-chip>
          </v-chip-group>

          <div v-if="isLoading" class="text-center py-8">
            <v-progress-circular indeterminate color="primary"/>
          </div>

          <template v-else>
            <NoData v-if="emptyState">{{ $t(emptyState) }}</NoData>

            <div v-else class="d-flex flex-column" style="gap: 14px;">
              <ApprovalQueueCard
              v-for="entry in displayedEntries"
              :key="entry.tractDueDiligenceApprovalId"
              :queueItem="entry"
              :conversionSlotWidth="conversionSlotWidth"
              @view="openView(entry)"/>
            </div>

            <div v-if="tab === 1 && hasMoreDecided" class="text-center mt-4">
              <v-btn text color="primary" :loading="decidedLoading" @click="fetchMoreDecided">
                <v-icon left>mdi-chevron-down</v-icon>{{ $t('loadMore') }}
              </v-btn>
            </div>
          </template>
        </material-card>
      </v-col>
    </v-row>

    <Dialog
    :stateId="dialogId"
    maxWidth="85vw"
    :contentClass="natureServeOpen ? 'ddq-dialog-shifted' : undefined"
    @click:outside="closeView"
    @dialog-closing="closeView"
    @close="closeView">
      <ViewDueDiligenceDialog
      v-if="focusedEntry"
      :queueItem="focusedEntry"
      @close="closeView"
      @decided="onDecided"
      @rejected="onRejected"
      @submission-updated="onSubmissionUpdated"
      @check-nature-serve="openNatureServe"/>
    </Dialog>

    <NatureServeSlideOver
    v-model="natureServeOpen"
    :species="natureServeSpecies"
    :stateCode="focusedEntry ? focusedEntry.countrySubdivision : null"/>
  </div>
</template>

<script>
import moment from 'moment'
import { mapActions } from 'vuex'
import { uniqueDialogId } from '@/utils/componentHelpers'
import { generateCsvString, exportCSVStringAsFile } from '@/utils/CSVUtility'
import { formatDate, utcToLocalDate, localToCompanyOffsetStartOfDay, localToCompanyOffsetEndOfDay } from '@/utils/DateFormatter.js'
import { DueDiligenceApprovalStatus, ConversionImpact } from '@/utils/Enumerations.js'
import { emptyDecidedFilter, riskPresenceFilterOptions, conversionSlotWidth } from '@/utils/dueDiligence/presentation.js'
import { dueDiligenceApprovalEnabled } from '@/utils/dueDiligence/mode.js'
import { Pagination } from '@/utils/constants.js'
import { cloneDeep } from 'lodash'

const PAGE_SIZE = 25
const EXPORT_PAGE_SIZE = Pagination.MaxPageSize

export default {
  name: 'DueDiligenceApproval',

  components: {
    Dialog: () => import('@/components/Dialog.vue'),
    ApprovalQueueCard: () => import('@/components/due-diligence/ApprovalQueueCard.vue'),
    ViewDueDiligenceDialog: () => import('@/components/due-diligence/ViewDueDiligenceDialog.vue'),
    NatureServeSlideOver: () => import('@/components/tract/tract-form/due-diligence/NatureServeSlideOver.vue'),
    DecidedApprovalsFilter: () => import('@/components/due-diligence/DecidedApprovalsFilter.vue'),
    Icon: () => import('@/components/helper/Icon.vue'),
    NoData: () => import('@/components/core/NoData.vue')
  },

  data: () => ({
    queue: [],
    loading: false,
    search: '',
    sortBy: 'dateSubmitted',
    teamFilter: null,
    dialogId: uniqueDialogId('due-diligence-approval'),
    focusedEntry: null,
    natureServeOpen: false,
    natureServeSpecies: null,
    // Nothing can be pending when the workflow is off, so open straight to the history. Pinning the
    // index rather than dropping the tab keeps every tab comparison below stable.
    tab: dueDiligenceApprovalEnabled() ? 0 : 1,
    // Decided history is fetched lazily on first tab activation, then paged with Load More
    decided: [],
    decidedLoading: false,
    decidedLoaded: false,
    decidedGeneration: 0,
    decidedPage: 0,
    hasMoreDecided: false,
    exporting: false,
    decidedFilter: emptyDecidedFilter()
  }),

  created () {
    this.refresh()
  },

  watch: {
    tab (value) {
      if (value === 1 && !this.decidedLoaded) this.fetchMoreDecided()
    },

    natureServeOpen (open) {
      if (!this.focusedEntry) return
      this.openOrUpdateDialog({ id: this.dialogId, width: open ? '72vw' : '85vw' })
    }
  },

  computed: {
    sortOptions () {
      return [
        { text: this.$t('dateSubmitted'), value: 'dateSubmitted' },
        { text: this.$t('tractName'), value: 'tractName' }
      ]
    },

    isLoading () {
      return this.tab === 0 ? this.loading : (this.decidedLoading && this.decided.length === 0)
    },

    emptyState () {
      if (this.tab === 0 && this.queue.length === 0) return 'noTractsAwaitingApproval'
      if (this.tab === 1 && this.decidedLoaded && this.decided.length === 0) return 'noDecidedApprovals'
      if (this.displayedEntries.length === 0) return 'noMatchingTracts'
      return null
    },

    searchTerm () {
      return (this.search ?? '').trim().toLowerCase()
    },

    teamFilterOptions () {
      const seen = new Map()
      for (const e of this.queue) {
        if (e.teamId != null && !seen.has(e.teamId)) seen.set(e.teamId, e.teamName ?? '')
      }
      const teams = [...seen.entries()]
        .map(([value, text]) => ({ value, text }))
        .sort((a, b) => a.text.localeCompare(b.text))
      return [{ text: this.$t('all'), value: null }, ...teams]
    },

    filteredQueue () {
      return this.queue.filter(e =>
        this.searchMatches(e) && (this.teamFilter == null || e.teamId === this.teamFilter))
    },

    filteredDecided () {
      return this.decided.filter(e => this.searchMatches(e, ['resolvedByUserName']))
    },

    sortedQueue () {
      const compare = this.sortBy === 'tractName'
        ? (a, b) => (a.tractName ?? '').localeCompare(b.tractName ?? '')
        : (a, b) => new Date(a.requestedAt) - new Date(b.requestedAt)
      return [...this.filteredQueue].sort(compare)
    },

    displayedEntries () {
      return this.tab === 0 ? this.sortedQueue : this.filteredDecided
    },

    conversionSlotWidth () {
      return conversionSlotWidth(this.tab === 0 ? this.queue : this.decided)
    },

    decidedFilterChips () {
      const f = this.decidedFilter
      const chips = []
      f.selectedTeams.forEach(t => chips.push({ key: `team-${t.teamId}`, type: 'team', id: t.teamId, icon: 'mdi-account-group', label: t.name }))
      f.selectedMitigationGroups.forEach(g => chips.push({ key: `group-${g.mitigationGroupId}`, type: 'group', id: g.mitigationGroupId, icon: 'mdi-square-rounded', iconColor: g.color, label: g.name }))
      if (f.riskPresence !== null) {
        const option = riskPresenceFilterOptions().find(o => o.value === f.riskPresence)
        chips.push({
          key: 'presence',
          type: 'presence',
          icon: option?.icon,
          label: `${this.$t('habitatRisk')}: ${option?.text}`
        })
      }
      if (f.status !== null) {
        const approved = f.status === DueDiligenceApprovalStatus.Approved.value
        chips.push({
          key: 'status',
          type: 'status',
          icon: approved ? 'mdi-check-decagram' : 'mdi-close-octagon',
          label: approved ? this.$t('approved') : this.$t('rejected')
        })
      }
      if (f.resolvedFrom || f.resolvedTo) {
        const from = f.resolvedFrom ? formatDate(f.resolvedFrom) : null
        const to = f.resolvedTo ? formatDate(f.resolvedTo) : null
        const label = from && to ? `${from} – ${to}` : (from ? `${this.$t('from')} ${from}` : `${this.$t('through')} ${to}`)
        chips.push({ key: 'dates', type: 'dates', icon: 'mdi-calendar-range', label })
      }
      return chips
    },

    decidedFilterParams () {
      const f = this.decidedFilter
      return {
        teamIds: f.selectedTeams.length ? f.selectedTeams.map(t => t.teamId) : undefined,
        mitigationGroupIds: f.selectedMitigationGroups.length ? f.selectedMitigationGroups.map(g => g.mitigationGroupId) : undefined,
        riskPresence: f.riskPresence ?? undefined,
        includeApproved: f.status == null || f.status === DueDiligenceApprovalStatus.Approved.value ? true : undefined,
        includeReturned: f.status == null || f.status === DueDiligenceApprovalStatus.Returned.value ? true : undefined,
        resolvedFrom: f.resolvedFrom ? localToCompanyOffsetStartOfDay(f.resolvedFrom) : undefined,
        resolvedTo: f.resolvedTo ? localToCompanyOffsetEndOfDay(f.resolvedTo) : undefined
      }
    },

    decidedCsvColumns () {
      return [
        { label: 'tract', value: e => e.tractName },
        { label: 'county', value: e => e.countrySecondarySubdivision },
        { label: 'state', value: e => e.countrySubdivision },
        { label: 'acres', value: e => e.acres },
        { label: 'harvestType', value: e => e.harvestTypeName },
        { label: 'loggingMethod', value: e => e.loggingMethodName },
        { label: 'conversion', value: e => e.conversion?.name },
        { label: 'conversionImpact', value: e => e.conversion ? ConversionImpact.fromInt(e.conversion.impact) : undefined },
        { label: 'team', value: e => e.teamName },
        { label: 'status', value: e => DueDiligenceApprovalStatus.fromInt(e.status) },
        { label: 'requestedBy', value: e => e.requestedByUserName },
        { label: 'requestedAt', value: e => e.requestedAt ? utcToLocalDate(e.requestedAt) : undefined },
        { label: 'resolvedBy', value: e => e.resolvedByUserName },
        { label: 'resolvedAt', value: e => e.resolvedAt ? utcToLocalDate(e.resolvedAt) : undefined },
        { label: 'reviewerComment', value: e => e.comment },
        { label: 'dueDiligenceExemption', value: e => e.dueDiligenceExemption?.name },
        { label: 'groupHits', value: e => (e.groupSpeciesCounts ?? []).map(h => h.mitigationGroupName).join('; ') },
        { label: 'habitatRiskPresence', value: e => e.anyRiskPresence ? this.$t('present') : this.$t('notPresent') }
      ]
    }
  },

  methods: {
    ...mapActions('due-diligence-approval', ['fetchApprovalQueue', 'fetchDecidedApprovals']),
    ...mapActions('dialog', ['openOrUpdateDialog', 'closeDialogsAtOrAbove']),

    dueDiligenceApprovalEnabled,

    async refresh () {
      if (dueDiligenceApprovalEnabled()) {
        this.loading = true
        try {
          this.queue = await this.fetchApprovalQueue() ?? []
          if (this.teamFilter != null && !this.queue.some(e => e.teamId === this.teamFilter)) this.teamFilter = null
        } catch (e) {
          console.error(e)
        } finally {
          this.loading = false
        }
      }
      this.resetDecided()
      if (this.tab === 1) await this.fetchMoreDecided()
    },

    searchMatches (entry, extraFields = []) {
      const term = this.searchTerm
      if (!term) return true
      return [
        entry.tractName,
        entry.countrySecondarySubdivision,
        entry.countrySubdivision,
        entry.requestedByUserName,
        ...extraFields.map(f => entry[f])
      ].some(field => (field ?? '').toLowerCase().includes(term))
    },

    resetDecided () {
      this.decidedGeneration++
      this.decided = []
      this.decidedPage = 0
      this.decidedLoaded = false
      this.hasMoreDecided = false
    },

    async fetchMoreDecided () {
      const generation = this.decidedGeneration
      const requestedPage = this.decidedPage + 1
      this.decidedLoading = true
      try {
        const result = await this.fetchDecidedApprovals({
          page: requestedPage,
          pageSize: PAGE_SIZE,
          ...this.decidedFilterParams
        })
        if (generation !== this.decidedGeneration) return
        this.decided = [...this.decided, ...(result?.items ?? [])]
        this.decidedPage = result?.page ?? requestedPage
        this.hasMoreDecided = this.decidedPage < (result?.totalPages ?? this.decidedPage)
        this.decidedLoaded = true
      } catch (e) {
        console.error(e)
      } finally {
        if (generation === this.decidedGeneration) this.decidedLoading = false
      }
    },

    async applyDecidedFilter (filter) {
      this.decidedFilter = filter
      this.resetDecided()
      await this.fetchMoreDecided()
    },

    removeDecidedFilter (chip) {
      const f = cloneDeep(this.decidedFilter)
      if (chip.type === 'team') f.selectedTeams = f.selectedTeams.filter(t => t.teamId !== chip.id)
      else if (chip.type === 'group') f.selectedMitigationGroups = f.selectedMitigationGroups.filter(g => g.mitigationGroupId !== chip.id)
      else if (chip.type === 'presence') f.riskPresence = null
      else if (chip.type === 'status') f.status = null
      else if (chip.type === 'dates') { f.resolvedFrom = null; f.resolvedTo = null }
      this.applyDecidedFilter(f)
    },

    async exportDecided () {
      this.exporting = true
      try {
        const firstPage = await this.fetchDecidedApprovals({ page: 1, pageSize: EXPORT_PAGE_SIZE, ...this.decidedFilterParams })
        const items = [...(firstPage?.items ?? [])]
        const totalPages = firstPage?.totalPages ?? 1
        for (let page = 2; page <= totalPages; page++) {
          const result = await this.fetchDecidedApprovals({ page, pageSize: EXPORT_PAGE_SIZE, ...this.decidedFilterParams })
          items.push(...(result?.items ?? []))
        }
        const matching = items.filter(e => this.searchMatches(e, ['resolvedByUserName']))
        const csvString = generateCsvString(matching, this.decidedCsvColumns)
        exportCSVStringAsFile(csvString, `DecidedDueDiligence_${moment().format('YYYY-MM-DD')}`)
      } catch (e) {
        console.error(e)
        this.setSnackError(this.$t('failedToExportDecided'))
      } finally {
        this.exporting = false
      }
    },

    openView (entry) {
      this.focusedEntry = entry
      this.openOrUpdateDialog({ id: this.dialogId, width: '85vw' })
    },

    closeView () {
      this.closeDialogsAtOrAbove(this.dialogId)
      this.focusedEntry = null
      this.natureServeOpen = false
    },

    async onDecided () {
      this.closeView()
      await this.refresh()
    },

    async onSubmissionUpdated () {
      await this.refresh()
      const updated = this.queue.find(e => e.tractDueDiligenceApprovalId === this.focusedEntry?.tractDueDiligenceApprovalId)
      if (updated) this.focusedEntry = updated
    },

    async onRejected () {
      this.closeView()
      await this.refresh()
    },

    openNatureServe (species) {
      this.natureServeSpecies = species
      this.natureServeOpen = true
    }
  }
}
</script>

<style>
.v-dialog.ddq-dialog-shifted:not(.v-dialog--fullscreen) {
  margin-right: calc(24vw + 12px);
}
</style>
